import React, { useState, useEffect } from "react";
import { apiClient } from "../../../utils/apiClient";
import { useAlert } from "../../../hooks/useAlert";
import { Modal } from "../../../components/common/ui/modal";

import { MultiSelect } from "../../../components/common/ui/multi-select";
import { OrganizationUser } from "../../../types";
import { useToast } from "../../../contexts/ToastContext";

interface AssignUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  organizationId: string;
  onSuccess?: () => void;
}

export const AssignUserModal: React.FC<AssignUserModalProps> = ({
  isOpen,
  onClose,
  caseId,
  organizationId,
  onSuccess,
}) => {
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const [members, setMembers] = useState<OrganizationUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMembers();
      setSelectedUserIds([]); // Reset selection when modal opens
    }
  }, [isOpen, organizationId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const orgMembers =
        await apiClient.listOrganizationMembers(organizationId);
      setMembers(orgMembers);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load organization members",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Please select at least one user to assign",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (selectedUserIds.length === 1) {
        // Single user assignment
        await apiClient.assignUserToCase(caseId, {
          userId: selectedUserIds[0],
        });
      } else {
        // Bulk user assignment
        await apiClient.assignUsersToCase(caseId, {
          assignments: selectedUserIds.map((userId) => ({
            userId,
          })),
        });
      }
      showToast(
        `${selectedUserIds.length} user${
          selectedUserIds.length > 1 ? "s" : ""
        } assigned to case successfully`,
      );

      setSelectedUserIds([]);
      onSuccess?.();
      onClose();
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to assign user(s)",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Users to Case"
      maxWidth="600px"
      primaryAction={{
        label: submitting
          ? "Assigning..."
          : `Assign ${
              selectedUserIds.length > 0 ? `${selectedUserIds.length} ` : ""
            }User${selectedUserIds.length !== 1 ? "s" : ""}`,
        onClick: handleSubmit,
        disabled: submitting || loading || selectedUserIds.length === 0,
        loading: submitting,
      }}
      secondaryAction={{
        label: "Cancel",
        disabled: submitting,
      }}
    >
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Select Users
        </label>
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading members...
          </p>
        ) : (
          <MultiSelect
            options={members.map((member) => ({
              value: member.userId,
              label: member.user?.fullName || member.user?.email || "Unknown",
              description: `Role: ${member.role}`,
            }))}
            selected={selectedUserIds}
            onChange={setSelectedUserIds}
            placeholder="Select users to assign..."
            searchPlaceholder="Search users..."
            disabled={submitting}
          />
        )}
      </div>
    </Modal>
  );
};

export default AssignUserModal;
